import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import LookupField, { LookupInputType } from '../../ui/lookup/LookupField';
import { TextRenderer, TextAlignLeftRenderer } from '../SmartTableRenderers';

import ApplicationContext from '../../ApplicationContext';
import { CompanyPreview } from '../../ifmsTypes';
import { SQLIndex } from '../../fms.core/constants';
import { TableColumn } from '../../tableSettings';

export interface CompanyLookupProps {
  initialValue: string | null | undefined,
  onSelect: (x: CompanyPreview) => void,
  placeholder?: string | undefined,
  onTextChange: (text: string | undefined) => void,
  filterName: string,
  disabled?: boolean,
  noBorder?: boolean,
  lookupId?: string | undefined,
  onTabNext?: (() => boolean) | undefined,
  onTabPrevious?: (() => boolean) | undefined,
  companyItems?: Array<CompanyPreview> | undefined,
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void,
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void,
  focusableWhenDisabled?: boolean,
  validationFor?: string | undefined,
  maxLength?: number | undefined,
  customColumn?: TableColumn[] | undefined,
  lookupType?:SQLIndex,
  getAllowAgentsList?: (() => any) | undefined,
  focusElementAfterSelect?: () => HTMLInputElement | HTMLElement | null,
  userInputType?: LookupInputType,
  hasLookupValue?:boolean,
  name?:string | undefined,
  noRightBorder?: boolean,
  noLeftBorder?: boolean,
  notFocusNextEleWhenSelectItem?: boolean,
};

function CompanyLookupField({
  initialValue = '',
  onSelect = () => { },
  placeholder = undefined,
  onTextChange = () => { },
  filterName,
  disabled = false,
  noBorder = false,
  lookupId = undefined,
  onTabNext = undefined,
  onTabPrevious = undefined,
  companyItems = undefined,
  onFocus = () => { },
  onBlur = () => { },
  focusableWhenDisabled = false,
  maxLength = undefined,
  customColumn = undefined,
  lookupType = SQLIndex.CompanyList,
  getAllowAgentsList = undefined,
  focusElementAfterSelect = () => null,
  userInputType = 0,
  hasLookupValue = false,
  noRightBorder = false,
  noLeftBorder = false,
  notFocusNextEleWhenSelectItem=false,
  ...others
}: CompanyLookupProps) {
  const appContext = useContext(ApplicationContext);

  async function queryCompanies(queryString: string | null) {
    if (getAllowAgentsList) { //for Invoice/Bill
      const lookupParams = await getAllowAgentsList();
      if (lookupParams.length >= 3) {
        lookupType = SQLIndex.CompanyListAgntParial
        queryString = lookupParams.join(',');
      }
    }

    const apiResult = await appContext.api.getLookupItems(lookupType, queryString ?? '', (filterName === 'fullName' ? 'FullName' : ''));
    return apiResult?.entities;
  }

  function onCancel() {
  }

  function filter(data: Array<CompanyPreview>, filterText: string) {
    return data.findIndex(x => x.shortName && x.shortName.toLowerCase().startsWith(filterText.toLowerCase()));
  }

  const columnsforWHP = [
    {
      Header: 'Agent',
      accessor: 'name',
      resizable: true,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 200,
      Cell: TextAlignLeftRenderer
    },
    {
      Header: 'Customer(WH)',
      accessor: 'companyRefNo',
      resizable: true,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 150,
      Cell: TextAlignLeftRenderer
    },
    {
      Header: 'Contact',
      accessor: 'contact',
      resizable: true,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 150,
      Cell: TextAlignLeftRenderer
    },
    {
      Header: 'Tel',
      accessor: 'tel',
      resizable: true,
      sortable: false,
      canBeHidden: false,
      visible: true,
      width: 150,
      Cell: TextAlignLeftRenderer
    },
  ];

  const columns = [
    // { Header: 'Id', accessor: 'id', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 30, Cell: TextRenderer },
    { Header: 'Short Name', accessor: 'shortName', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 180, Cell: TextAlignLeftRenderer },
    { Header: 'CompanyRefNo', accessor: 'companyRefNo', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 120, Cell: TextAlignLeftRenderer },
    { Header: 'Contact', accessor: 'contact', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 180, Cell: TextAlignLeftRenderer },
    { Header: 'FIRMS Code', accessor: 'iataNo', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 80, Cell: TextRenderer },
    { Header: 'Full Name', accessor: 'fullName', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 250, Cell: TextAlignLeftRenderer },
  ];

  const columnsForDelivery = [
    // { Header: 'Id', accessor: 'id', resizable: false, sortable: false, canBeHidden: false, visible: true, width: 30, Cell: TextRenderer },
    { Header: 'Full Name', accessor: 'fullName', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 250, Cell: TextAlignLeftRenderer },
    { Header: 'CompanyRefNo', accessor: 'companyRefNo', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 120, Cell: TextAlignLeftRenderer },
    { Header: 'Contact', accessor: 'contact', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 180, Cell: TextAlignLeftRenderer },
    { Header: 'FIRMS Code', accessor: 'iataNo', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 80, Cell: TextRenderer },
    { Header: 'Short Name', accessor: 'shortName', resizable: true, sortable: false, canBeHidden: false, visible: true, width: 180, Cell: TextAlignLeftRenderer },
  ];

  return (
    <LookupField
      lookupId={lookupId}
      onTabNext={onTabNext}
      onTabPrevious={onTabPrevious}
      disabled={disabled}
      query={queryCompanies}
      width={lookupId === 'WHP' ? "650pxpx" : "826px"}
      columns={lookupId === 'WHP' ? columnsforWHP : (filterName === 'fullName' ? columnsForDelivery : columns)}
      onSelect={onSelect}
      onCancel={onCancel}
      labelProp="shortName"
      initialValue={initialValue}
      placeholder={placeholder}
      onTextChange={onTextChange}
      filter={filter}
      onBlur={onBlur}
      onFocus={onFocus}
      focusableWhenDisabled={focusableWhenDisabled}
      maxLength={maxLength}
      noBorder={noBorder}
      noRightBorder={noRightBorder}
      noLeftBorder={noLeftBorder}
      focusElementAfterSelect={focusElementAfterSelect}
      userInputType={userInputType}
      {...others}
      hasLookupValue={hasLookupValue} 
    />
  );
}

CompanyLookupField.defaultProps = {
  onSelect: () => { },
  onTextChange: () => { },
  filterName: 'shortName',
  disabled: false,
  focusableWhenDisabled: false,
  hasLookupValue: false
};

CompanyLookupField.propTypes = {
  initialValue: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onTextChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  filterName: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  lookupId: PropTypes.string,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  companyItems: PropTypes.array,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  focusableWhenDisabled: PropTypes.bool.isRequired,
  customColumn: PropTypes.array,
  hasLookupValue: PropTypes.bool
};


export default React.memo(CompanyLookupField);
